import { useMutation, useQueryClient } from '@tanstack/react-query';
import { bulkReRateService } from '@/services/trades/bulk-rerate-service';
import { tradesKeys } from '@/services/query-keys/trades-keys';
import { billingKeys } from '@/services/query-keys/billing-keys';
import { useToastStore } from '@/stores/toast-store';
import type { BulkReRateRequest, BulkReRateResponse } from '@/types';

/**
 * LR-539: atomic bulk re-rate via POST /positions/bulk_re_rate/. Invalidates the
 * positions cache (list + detail + activity share the ['positions'] prefix) and
 * the accruals prefix (a T0 re-rate moves today's accrual to the new rate).
 *
 * NOTE: do NOT toast/invalidate on error — all-or-nothing means nothing was
 * written; the staging modal parses the 400 failed[] body from mutation.error.
 */
export const useBulkReRate = () => {
  const queryClient = useQueryClient();
  const addToast = useToastStore((s) => s.addToast);

  return useMutation<BulkReRateResponse, Error, BulkReRateRequest>({
    mutationFn: (request) => bulkReRateService.bulkReRate(request),
    onSuccess: (resp) => {
      const n = resp.re_rated + resp.booked;
      const verb = resp.re_rated === 0 && resp.booked > 0 ? 'booked' : 're-rated';
      addToast('success', `${n} ${n === 1 ? 'trade' : 'trades'} ${verb}`);
      queryClient.invalidateQueries({ queryKey: tradesKeys.all });
      queryClient.invalidateQueries({ queryKey: billingKeys.accruals() });
    },
  });
};
